/**
 * api-fetch — the ONE place the SPA talks to the daemon.
 *
 * Every read and the one mutation funnel through {@link apiFetch}, which is what
 * makes the credential story checkable rather than hopeful: there is a single
 * line that decides what gets sent, so a new endpoint cannot quietly ship
 * anonymous. Three things are decided here.
 *
 * **Credentials.** `credentials: 'same-origin'` is set explicitly. It matches
 * `fetch`'s default, and that is the point — the daemon is now reached only
 * through the oauth2-proxy session cookie, so the behaviour every call depends
 * on must be stated at the call site instead of inherited from a default that a
 * future absolute URL or a stricter default would silently change.
 *
 * **Redirects.** `redirect: 'manual'`. When oauth2-proxy runs with
 * `--skip-provider-button=true` (the Google configuration), an unauthenticated
 * request to a guarded route is answered with a `302` to Google's authorization
 * endpoint — not the daemon's `401`. Left on `follow`, `fetch` chases that
 * cross-origin redirect and fails CORS, surfacing as an indistinguishable
 * `TypeError` that reads like a network blip: the board would age to
 * "disconnected" for a user who is merely signed out. `manual` turns it into an
 * `opaqueredirect` we can classify. Under the htpasswd bridge the same user gets
 * an inline `403` sign-in form instead, so BOTH shapes must classify as
 * signed-out — see `session.ts`, which decides that on the response body rather
 * than on which proxy flag happens to be set.
 *
 * **Identity failures are typed.** `401`/`403`/redirect throw
 * {@link SessionError}, a {@link ReadApiError} subclass, so the pre-existing
 * `instanceof ReadApiError` handling keeps working unchanged while the App can
 * narrow to the session case and render a real signed-out / not-permitted state.
 * Everything else (`404`, `409`, `503`, `304`) is returned untouched — those are
 * the callers' own semantics, not identity. The body is read here ONLY for the
 * statuses that could be an identity failure: it is a one-shot stream, and
 * draining it on a `304` or `404` would rob the caller of its own payload.
 */

import { type SessionFailureKind, classifySessionFailure, isPossibleIdentityFailure } from './session'

/** A read fetch failed — carries the endpoint + status so the UI can name it honestly. */
export class ReadApiError extends Error {
  constructor(
    readonly endpoint: string,
    readonly status: number,
    message: string
  ) {
    super(message)
    this.name = 'ReadApiError'
  }
}

/**
 * A call failed on WHO the caller is, not on what they asked for. A
 * {@link ReadApiError} so existing error paths (polling state, drawer error
 * panels) keep rendering it honestly; `kind` is what the session gate branches
 * on. `signed-out` leads to the proxy sign-in, `not-permitted` never does.
 */
export class SessionError extends ReadApiError {
  constructor(
    endpoint: string,
    status: number,
    readonly kind: SessionFailureKind,
    message: string
  ) {
    super(endpoint, status, message)
    this.name = 'SessionError'
  }
}

/** Narrow any thrown value to its session-failure kind, or null if it is not one. */
export function sessionFailureOf(err: unknown): SessionFailureKind | null {
  return err instanceof SessionError ? err.kind : null
}

/** Default per-request budget (ms) — a reachable-but-unresponsive daemon must not strand the UI. */
export const DEFAULT_TIMEOUT_MS = 10_000

export interface ApiFetchOptions {
  /** The endpoint path, used verbatim as the request URL and as the error label. */
  endpoint: string
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  /** Injection point for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
  timeoutMs?: number
}

/**
 * Issue one credentialed, time-bounded request to the daemon and return the
 * `Response` for the caller to decode. Throws {@link SessionError} on an
 * identity failure and {@link ReadApiError} on a transport failure or timeout;
 * every other status is the caller's to interpret.
 */
export async function apiFetch(options: ApiFetchOptions): Promise<Response> {
  const { endpoint, method = 'GET', headers, fetchImpl = fetch, timeoutMs = DEFAULT_TIMEOUT_MS } = options

  // Bound the request: a daemon that is TCP-reachable but unresponsive would
  // otherwise leave this Promise pending forever, stranding the UI on its
  // loading state. On timeout the AbortError surfaces through the catch as a
  // ReadApiError, so the caller renders the honest error state.
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  let res: Response
  try {
    res = await fetchImpl(endpoint, {
      method,
      ...(headers === undefined ? {} : { headers }),
      // The oauth2-proxy session cookie is the only credential the browser
      // holds; it is httpOnly, so JS can neither read nor attach it — sending
      // it is entirely a matter of not suppressing it. Stated, not assumed.
      credentials: 'same-origin',
      // See the module header: a sign-in bounce must stay readable as a
      // redirect instead of exploding as a CORS failure.
      redirect: 'manual',
      signal: controller.signal,
    })
  } catch (err) {
    throw new ReadApiError(endpoint, 0, err instanceof Error ? err.message : 'network error')
  } finally {
    clearTimeout(timeoutId)
  }

  // Reading the body is what separates the daemon's "you are barred" 403 from
  // the proxy's inline sign-in form, so it must happen BEFORE classification —
  // but only for statuses that could be an identity failure, since `res.json()`
  // consumes a stream every other caller still needs.
  if (!isPossibleIdentityFailure(res.status, res.type)) {
    return res
  }
  const errorCode = await readErrorCode(res)
  const failure = classifySessionFailure(res.status, res.type, errorCode)
  if (failure !== null) {
    throw new SessionError(endpoint, res.status, failure, sessionMessage(endpoint, res.status, failure, errorCode))
  }
  return res
}

/**
 * Human-readable text for an identity failure, enriched with the daemon's own
 * error code when it sent one (`missing_token`, `invalid_token`,
 * `principal_not_permitted`) so the operator-facing message names the actual
 * cause. The code is passed in rather than re-read: the body has already been
 * consumed by then, and reading it twice would silently yield null.
 */
function sessionMessage(endpoint: string, status: number, failure: SessionFailureKind, code: string | null): string {
  const base =
    failure === 'not-permitted'
      ? `${endpoint} refused this account (${status})`
      : `${endpoint} requires a signed-in session (${status === 0 ? 'redirected to sign-in' : status})`
  return code === null ? base : `${base}: ${code}`
}

/**
 * Best-effort read of the daemon's `{"error": "<code>"}` body; null when
 * unavailable. Null is load-bearing, not merely defensive: an opaque redirect
 * has no readable body, and the proxy's sign-in page is HTML, so both land here
 * and both must classify as signed-out rather than terminal.
 */
async function readErrorCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { error?: unknown }
    return typeof body.error === 'string' ? body.error : null
  } catch {
    return null
  }
}
